import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { resolveActiveCustomerContext } from '../auth/customerContext';
import { DashboardPageShell, DashboardPanel } from '../components/dashboard';
import '../components/dashboard/dashboardFleet.css';
import { CompanyDetailsContent } from './CompanyDetailsPage';
import { BillingContent } from './BillingPage';
import MaintenanceSettingsCard from './MaintenanceSettingsCard';
import OperatingScheduleSettings from './OperatingScheduleSettings';
import './SettingsPage.css';

const OWNER_CATEGORIES = [
  { id: 'company-profile', label: 'Company / Profile' },
  { id: 'operations', label: 'Operations' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'billing-subscription', label: 'Billing / Subscription' },
];
const MANAGER_CATEGORIES = OWNER_CATEGORIES.filter(({ id }) => id === 'maintenance');

function hashCategory(categories) {
  const hash = window.location.hash.replace(/^#/, '');
  return categories.some(({ id }) => id === hash) ? hash : categories[0].id;
}

export default function SettingsPage() {
  const { session } = useAuth();
  const customerName = resolveActiveCustomerContext(session).customerName;
  const isOwner = String(session?.customerAccountRole || '').toUpperCase() === 'OWNER';
  const categories = useMemo(() => isOwner ? OWNER_CATEGORIES : MANAGER_CATEGORIES, [isOwner]);
  const [activeCategory, setActiveCategory] = useState(() => hashCategory(categories));

  useEffect(() => {
    const selectHashCategory = () => setActiveCategory(hashCategory(categories));
    selectHashCategory();
    window.addEventListener('hashchange', selectHashCategory);
    return () => window.removeEventListener('hashchange', selectHashCategory);
  }, [categories]);

  const selectCategory = (id) => {
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${id}`);
    setActiveCategory(id);
  };

  return <DashboardPageShell title={customerName} productDashboardShell="customer">
    <DashboardPanel className="dashboard-fleet-dashboard-panel customer-settings-panel" aria-live="polite">
      <header className="dashboard-fleet-section-heading customer-users-header customer-settings-page-heading">
        <div>
          <h1 className="dashboard-title">Settings</h1>
          <p className="dashboard-subtitle">Manage company, operating, maintenance, and billing settings for your account.</p>
        </div>
        <div className="customer-users-header-actions customer-settings-header-actions">
          <nav className="fleet-toggle maintenance-tabs customer-settings-navigation" aria-label="Settings categories" role="tablist">
            {categories.map(({ id, label }) => <button
              key={id}
              type="button"
              role="tab"
              className="fleet-toggle-button"
              aria-selected={activeCategory === id}
              onClick={() => selectCategory(id)}
            >{label}</button>)}
          </nav>
        </div>
      </header>

      <div className="customer-settings-content">
        {activeCategory === 'company-profile' && <section aria-labelledby="company-profile-heading">
          <header className="customer-settings-heading">
            <h2 id="company-profile-heading">Company / Profile</h2>
            <p>Manage the company identity and facility information shown throughout ManageLift.</p>
          </header>
          <CompanyDetailsContent showOperationsLink={false} />
        </section>}

        {activeCategory === 'operations' && <section aria-labelledby="operations-heading">
          <header className="customer-settings-heading"><h2 id="operations-heading">Operations</h2></header>
          <OperatingScheduleSettings />
        </section>}

        {activeCategory === 'maintenance' && <section aria-labelledby="maintenance-heading">
          <header className="customer-settings-heading"><h2 id="maintenance-heading">Maintenance</h2></header>
          <MaintenanceSettingsCard customerId={session?.customerId} />
        </section>}

        {activeCategory === 'billing-subscription' && <section aria-labelledby="billing-heading">
          <header className="customer-settings-heading">
            <h2 id="billing-heading">Billing / Subscription</h2>
            <p>Manage your ManageLift subscription, invoices, and payment methods through Stripe.</p>
          </header>
          <BillingContent showHeading={false} />
        </section>}
      </div>
    </DashboardPanel>
  </DashboardPageShell>;
}
